import styled from 'styled-components';

import { ScArrowButton } from '@/components/_promo/components/ArrowButton/styled';
import { ScButton } from '@/components/_promo/components/buttons/styled';
import { ScCopyCode } from '@/components/_promo/components/DashedCopyCode/styled';
import { device, hexToRGB, typography } from '@/styles/helpers';
import type { OfferCardConfig } from './helpers';

interface IScOfferCardProps {
  cardRotate: boolean;
  cardTheme?: OfferCardConfig;
  withCards?: boolean;
  coinPositions?: Partial<
    Record<
      'coins-1' | 'coins-2' | 'coins-3',
      { left?: string; right?: string; top?: string; bottom?: string }
    >
  >;
}

export const ScOfferCard = styled.div<IScOfferCardProps>`
  perspective: 1000px;
  position: relative;
  width: 100%;
  max-width: 380px;
  height: ${({ cardTheme }) => `${cardTheme?.height.desktop}px`};
  @media (${device.mobile}) {
    height: ${({ cardTheme }) => `${cardTheme?.height.mobile}px`};
    max-width: 340px;
  }

  .card-inner {
    position: relative;
    width: 100%;
    height: ${({ cardTheme }) => `${cardTheme?.height.desktop}px`};
    transition: transform 0.8s ease-in-out;
    transform-style: preserve-3d;
    -webkit-transform-style: preserve-3d;
    transform: ${({ cardRotate }) =>
      cardRotate ? 'rotateY(180deg)' : 'rotateY(0deg)'};
    @media (${device.mobile}) {
      height: ${({ cardTheme }) => `${cardTheme?.height.mobile}px`};
    }
  }

  .card-front,
  .card-back {
    position: absolute;
    width: 100%;
    height: 100%;
    border-radius: 20px;
    backface-visibility: hidden;
    -webkit-backface-visibility: hidden;
    top: 0;
    left: 0;
    padding: 16px 16px 0 16px;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    justify-content: center;
    text-align: center;
  }

  .card-front {
    background: ${({ cardTheme }) => cardTheme?.background.color};
    outline: 2px solid ${({ cardTheme }) => cardTheme?.border.color};
    opacity: ${({ cardRotate }) => (cardRotate ? 0 : 1)};
    pointer-events: ${({ cardRotate }) => (cardRotate ? 'none' : 'auto')};
    transition: opacity 0.4s ease-in-out;
    justify-content: start;

    ${ScArrowButton} {
      color: ${({ theme }) => hexToRGB(theme.whiteColor, 0.6)};
      margin-top: ${({ withCards }) => (withCards ? '8px' : '0')};
      @media (${device.mobile}) {
        margin-top: ${({ withCards }) => (withCards ? '9px' : '0')};
      }
    }

    & .reg-btn {
      display: block;
      @media (${device.mobile}) {
        display: none;
      }
    }

    & .title {
      ${typography['headline3']};
      text-transform: uppercase;
      color: ${({ theme }) => hexToRGB(theme.whiteColor, 0.6)};
    }
    & .offer-count-fs {
      font-size: 160px;
      font-weight: 800;
      line-height: 130px;
      letter-spacing: 4.8px;
      background: ${({ cardTheme }) => cardTheme?.text.color};
      -webkit-background-clip: text;
      background-clip: text;
      color: transparent;
      -webkit-text-fill-color: transparent;
      @media (${device.mobile}) {
        font-size: 148px;
        letter-spacing: 4.44px;
      }
    }
    & .offer-fs {
      font-size: 32px;
      font-weight: 700;
      line-height: 40px;
      letter-spacing: 0.96px;
      background: ${({ cardTheme }) => cardTheme?.text.color};
      -webkit-background-clip: text;
      background-clip: text;
      color: transparent;
      -webkit-text-fill-color: transparent;
    }
    & .benefits-container {
      display: flex;
      gap: 32px;
      justify-content: center;
      margin-top: 8px;
      .benefits-wrapper {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 3px;
        font-size: 14px;
        font-weight: 700;
        line-height: 18px;
        letter-spacing: 0.14px;
        text-transform: uppercase;
      }
    }
    ${ScButton} {
      height: 56px;
      max-width: none;
    }

    .terms-trigger {
      margin-top: 16px;
      text-decoration: underline;
      cursor: pointer;
      color: #fff;
      font-size: 14px;
      opacity: 0.8;
      &:hover {
        opacity: 1;
      }
    }
    .coins-1,
    .coins-2,
    .coins-3 {
      position: absolute;
      z-index: 10;
    }

    .coins-1 {
      left: ${({ coinPositions }) =>
        coinPositions?.['coins-1']?.left || '-60px'};
      right: ${({ coinPositions }) =>
        coinPositions?.['coins-1']?.right || 'auto'};
      top: ${({ coinPositions }) => coinPositions?.['coins-1']?.top || '200px'};
      bottom: ${({ coinPositions }) =>
        coinPositions?.['coins-1']?.bottom || 'auto'};
    }
    .coins-2 {
      left: ${({ coinPositions }) =>
        coinPositions?.['coins-2']?.left || 'auto'};
      right: ${({ coinPositions }) =>
        coinPositions?.['coins-2']?.right || '-30px'};
      top: ${({ coinPositions }) => coinPositions?.['coins-2']?.top || '20px'};
      bottom: ${({ coinPositions }) =>
        coinPositions?.['coins-2']?.bottom || 'auto'};
    }
    .coins-3 {
      left: ${({ coinPositions }) =>
        coinPositions?.['coins-3']?.left || 'auto'};
      right: ${({ coinPositions }) =>
        coinPositions?.['coins-3']?.right || '-30px'};
      top: ${({ coinPositions }) => coinPositions?.['coins-3']?.top || 'auto'};
      bottom: ${({ coinPositions }) =>
        coinPositions?.['coins-3']?.bottom || '-10px'};
    }
    .copy-code-wrapper {
      height: 32px;
      margin-top: 22px;
      ${ScCopyCode} {
        button {
          height: 32px;
        }
      }

      ${ScCopyCode} {
        .notranslate {
          padding: 4px 48px 0 16px !important;
        }
      }

      div {
        margin-bottom: 0;
      }
    }
    & .register-btn {
      margin: 24px 0 0 0 !important;
      max-width: none !important;
      min-width: 0;
      .text {
        font-size: 24px !important;
        line-height: 32px;
        font-weight: 700;
      }
    }

    & .img-cards-wrapper {
      display: flex;
      gap: 4px;
      justify-content: center;
    }
  }

  .card-back {
    background: ${({ cardTheme }) => cardTheme?.background.color};
    outline: 2px solid ${({ cardTheme }) => cardTheme?.border.color};
    opacity: ${({ cardRotate }) => (cardRotate ? 1 : 0)};
    pointer-events: ${({ cardRotate }) => (cardRotate ? 'auto' : 'none')};
    transition: opacity 0.4s ease-in-out;
    transform: rotateY(180deg);
    padding: 16px 12px 0 24px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: flex-start;
    position: relative;

    ${ScArrowButton} {
      color: ${({ theme }) => theme.whiteColor};
    }

    .terms-wrapper {
      max-height: 420px;
      overflow-y: auto;
      overflow-x: hidden;

      scrollbar-gutter: stable both-edges;
      padding-right: 12px;

      &::-webkit-scrollbar {
        width: 4px;
      }

      &::-webkit-scrollbar-track {
        background: transparent;
        margin-right: 12px;
      }

      &::-webkit-scrollbar-thumb {
        background-color: #b2aed2;
        border-radius: 10px;
        border: none;
      }
    }

    .back-btn {
      background: none;
      border: none;
      font-size: 16px;
      cursor: pointer;
      color: #007aff;
      display: flex;
      align-items: center;
      font-weight: 600;
    }
    .coins-1,
    .coins-2,
    .coins-3 {
      position: absolute;
      z-index: 10;
    }

    .coins-1 {
      left: ${({ coinPositions }) =>
        coinPositions?.['coins-1']?.left || '-60px'};
      right: ${({ coinPositions }) =>
        coinPositions?.['coins-1']?.right || 'auto'};
      top: ${({ coinPositions }) => coinPositions?.['coins-1']?.top || '200px'};
      bottom: ${({ coinPositions }) =>
        coinPositions?.['coins-1']?.bottom || 'auto'};
    }
    .coins-2 {
      left: ${({ coinPositions }) =>
        coinPositions?.['coins-2']?.left || 'auto'};
      right: ${({ coinPositions }) =>
        coinPositions?.['coins-2']?.right || '-30px'};
      top: ${({ coinPositions }) => coinPositions?.['coins-2']?.top || '20px'};
      bottom: ${({ coinPositions }) =>
        coinPositions?.['coins-2']?.bottom || 'auto'};
    }
    .coins-3 {
      left: ${({ coinPositions }) =>
        coinPositions?.['coins-3']?.left || 'auto'};
      right: ${({ coinPositions }) =>
        coinPositions?.['coins-3']?.right || '-30px'};
      top: ${({ coinPositions }) => coinPositions?.['coins-3']?.top || 'auto'};
      bottom: ${({ coinPositions }) =>
        coinPositions?.['coins-3']?.bottom || '-10px'};
    }
  }
`;
